import type {
  PetChassisFinishWire,
  PetEnergyColorWire,
  PetPaletteResponse,
  PetStateWire,
  PetTrainingWire,
} from '@kenocyber/shared';
import { PET_FOOD_ATTACK_SPEED_ID, PET_FOOD_DAMAGE_ID } from '@kenocyber/shared';
import { useState } from 'react';

/**
 * Training and appearance, inside the pet's own panel.
 *
 * Three sections, and each one answers the question a player actually has:
 *
 * - **Treinamento** - what have I fed it, what is the bonus, what would ONE
 *   more do. The before/after is the whole point: a percentage on its own is
 *   not a decision, and a percentage next to the one it would become is.
 * - **Cor dos efeitos** and **Lataria** - what exists, what do I own, what am
 *   I wearing. Locked entries are shown rather than hidden, with the item that
 *   opens them and how many of it are in hand, because a locked option is
 *   information and an absent one is a mystery.
 *
 * Nothing here decides anything. Every button sends a request and re-renders
 * from the answer; the counters, the bonuses and the unlocks are all the
 * server's to state.
 */

interface PetCustomisationProps {
  readonly pet: PetStateWire;
  /** The copy being trained and painted - the active one, or the best. */
  readonly copyInstanceId: string;
  readonly training: PetTrainingWire;
  readonly energyId: string;
  readonly finishId: string;
  readonly palette: PetPaletteResponse | null;
  readonly busy: boolean;
  /**
   * Which sections to show.
   *
   * The panel above splits training and appearance into their own tabs, so a
   * player is never handed a wall of controls; `'all'` keeps the original
   * single-column form for anything that still wants it.
   */
  readonly show?: 'all' | 'training' | 'appearance';
  readonly onFeed: (foodDefinitionId: string) => void;
  readonly onUnlock: (unlockDefinitionId: string) => void;
  readonly onApply: (energyId: string, finishId: string) => void;
  /**
   * Show this pair on the TURNTABLE without applying it.
   *
   * Absent in contexts with no preview. Selecting a swatch to look at must
   * never repaint the pet in the arena, so previewing and applying are separate
   * callbacks rather than one callback with a flag.
   */
  readonly onPreview?: (kind: 'energy' | 'finish', id: string) => void;
}

/** Basis points as a percentage, at one decimal: 150 -> `1,5%`. */
function bps(value: number): string {
  return `${(value / 100).toFixed(1).replace('.', ',')}%`;
}

export function PetCustomisation({
  pet,
  copyInstanceId,
  training,
  energyId,
  finishId,
  palette,
  busy,
  show = 'all',
  onFeed,
  onUnlock,
  onApply,
  onPreview,
}: PetCustomisationProps) {
  /** Which food the player is confirming, if any. Consuming is deliberate. */
  const [confirmFood, setConfirmFood] = useState<string | null>(null);
  const [confirmUnlock, setConfirmUnlock] = useState<string | null>(null);

  if (!palette) {
    return (
      <p className="inv-state" data-testid="pet-palette-loading">
        Carregando personalização…
      </p>
    );
  }

  const damageStock = Number(palette.damageFoodStock);
  const speedStock = Number(palette.attackSpeedFoodStock);

  /** One food row: used, current bonus, stock, and what one more would give. */
  const foodRow = (
    id: string,
    label: string,
    used: number,
    current: number,
    perUnit: number,
    stock: number,
    testId: string,
  ) => (
    <div className="pet-train-row" data-testid={`pet-train-${testId}`}>
      <div className="pet-train-head">
        <span className="inv-detail-label">{label}</span>
        <span className="inv-detail-value" data-testid={`pet-train-${testId}-used`}>
          {used} usados
        </span>
      </div>
      <div className="pet-train-numbers">
        <span data-testid={`pet-train-${testId}-bonus`}>{bps(current)}</span>
        {/* The before/after. A bonus on its own is not a decision. */}
        <span className="pet-train-arrow" aria-hidden="true">
          →
        </span>
        <span className="pet-train-next" data-testid={`pet-train-${testId}-next`}>
          {bps(current + perUnit)}
        </span>
      </div>
      <div className="pet-train-actions">
        <span className="pet-stock" data-testid={`pet-train-${testId}-stock`}>
          {stock} em estoque
        </span>
        <button
          type="button"
          className="inv-equip"
          data-testid={`pet-feed-${testId}`}
          disabled={busy || stock < 1}
          onClick={() => setConfirmFood(id)}
        >
          ALIMENTAR
        </button>
      </div>
      {confirmFood === id ? (
        <div className="inv-confirm" data-testid={`pet-feed-confirm-${testId}`}>
          <p>
            Alimentar {pet.name}: {bps(current)} → {bps(current + perUnit)}?
          </p>
          <p className="art-combine-warning">Uma unidade é consumida permanentemente.</p>
          <div className="inv-confirm-actions">
            <button
              type="button"
              data-testid={`pet-feed-confirm-yes-${testId}`}
              disabled={busy}
              onClick={() => {
                setConfirmFood(null);
                onFeed(id);
              }}
            >
              {busy ? 'Alimentando…' : 'Confirmar'}
            </button>
            <button type="button" onClick={() => setConfirmFood(null)}>
              Cancelar
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );

  /**
   * One appearance swatch.
   *
   * Locked entries show a padlock, the item that opens them and how many are
   * in hand - never a disabled blank that pretends the option does not exist.
   */
  const swatch = (
    entry: PetEnergyColorWire | PetChassisFinishWire,
    kind: 'energy' | 'finish',
    current: boolean,
    stock: number,
  ) => {
    const hex = `#${entry.color.toString(16).padStart(6, '0')}`;
    return (
      <div
        key={entry.id}
        className="pet-swatch"
        data-kind={kind}
        data-locked={entry.unlocked ? 'false' : 'true'}
        data-current={current ? 'true' : 'false'}
        data-testid={`pet-swatch-${entry.id}`}
      >
        {/* The chip PREVIEWS. Looking is free and reversible; wearing is
            neither, so the two are different controls. */}
        <button
          type="button"
          className="pet-swatch-chip"
          style={{ background: hex }}
          data-testid={`pet-swatch-chip-${entry.id}`}
          disabled={!onPreview || !entry.unlocked}
          title={entry.unlocked ? `Pré-visualizar ${entry.name}` : entry.name}
          // ONE AXIS at a time: the caller merges it over whatever is already
          // being previewed. Sending a whole pair made each click reset the
          // other axis back to the worn one.
          onClick={() => onPreview?.(kind, entry.id)}
        >
          {entry.unlocked ? null : <span className="pet-swatch-lock">🔒</span>}
        </button>
        <span className="pet-swatch-name">{entry.name}</span>
        {current ? (
          <span className="pet-swatch-current" data-testid={`pet-swatch-current-${entry.id}`}>
            ATUAL
          </span>
        ) : entry.unlocked ? (
          <button
            type="button"
            className="pet-swatch-action"
            data-testid={`pet-apply-${entry.id}`}
            disabled={busy}
            onClick={() =>
              kind === 'energy' ? onApply(entry.id, finishId) : onApply(energyId, entry.id)
            }
          >
            APLICAR
          </button>
        ) : (
          <>
            <span className="pet-swatch-need" data-testid={`pet-swatch-stock-${entry.id}`}>
              {stock} item
            </span>
            <button
              type="button"
              className="pet-swatch-action"
              data-testid={`pet-unlock-${entry.id}`}
              disabled={busy || stock < 1}
              onClick={() => setConfirmUnlock(entry.unlockItemId)}
            >
              DESBLOQUEAR
            </button>
          </>
        )}
        {confirmUnlock === entry.unlockItemId ? (
          <div className="inv-confirm" data-testid={`pet-unlock-confirm-${entry.id}`}>
            <p>Desbloquear {entry.name} para a conta?</p>
            <p className="art-combine-warning">O item é consumido uma única vez.</p>
            <div className="inv-confirm-actions">
              <button
                type="button"
                data-testid={`pet-unlock-confirm-yes-${entry.id}`}
                disabled={busy}
                onClick={() => {
                  setConfirmUnlock(null);
                  onUnlock(entry.unlockItemId);
                }}
              >
                {busy ? 'Desbloqueando…' : 'Confirmar'}
              </button>
              <button type="button" onClick={() => setConfirmUnlock(null)}>
                Cancelar
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="pet-custom" data-testid="pet-customisation" data-copy={copyInstanceId}>
      <section
        className="pet-custom-section"
        data-testid="pet-training-section"
        hidden={show === 'appearance'}
      >
        <h4>Treinamento</h4>
        {foodRow(
          PET_FOOD_DAMAGE_ID,
          'Dano',
          training.damageFoodCount,
          training.damageBonusBps,
          100,
          damageStock,
          'damage',
        )}
        {foodRow(
          PET_FOOD_ATTACK_SPEED_ID,
          'Cadência',
          training.attackSpeedFoodCount,
          training.attackSpeedBonusBps,
          50,
          speedStock,
          'speed',
        )}
      </section>

      <section
        className="pet-custom-section"
        data-testid="pet-energy-section"
        hidden={show === 'training'}
      >
        <h4>Cor dos efeitos</h4>
        <div className="pet-swatches">
          {palette.energies.map((entry) =>
            swatch(entry, 'energy', entry.id === energyId, Number(entry.stock)),
          )}
        </div>
      </section>

      <section
        className="pet-custom-section"
        data-testid="pet-finish-section"
        hidden={show === 'training'}
      >
        <h4>Lataria</h4>
        <div className="pet-swatches">
          {palette.finishes.map((entry) =>
            swatch(entry, 'finish', entry.id === finishId, Number(entry.stock)),
          )}
        </div>
      </section>
    </div>
  );
}
